import { useStore } from "@/state/store";
import { fmtMarketMetric, fmtShortDate } from "@/components/atoms/format";
import { marketMetrics, type MarketWithMetrics } from "@/api/marketMetrics";
import { useMid } from "./useMid";

type MarketView = MarketWithMetrics;

interface Props {
  markets: MarketView[];
}

// Grid template: title (flex) | category | probability | volume | liquidity |
// YES/NO buttons. La colonna trend (sparkline) è rimossa finché non avremo
// veri dati di history.
// Le colonne fisse sono strette di proposito: con volume + liquidità la somma
// resta vicina a quella precedente, così la colonna title non viene schiacciata.
const GRID = "minmax(0, 3fr) 100px 90px 100px 100px 200px";

// Sotto questa soglia la tabella scrolla in orizzontale invece di collassare
// la colonna title a 0px (e tagliare i bottoni) come faceva prima.
const MIN_W = "min-w-[860px]";

export function MarketTable({ markets }: Props) {
  return (
    <div className="card overflow-x-auto p-0">
      <div className={MIN_W}>
        <div
          className="grid items-center gap-3 border-b border-line bg-bg-2 px-4 py-2 text-[11px] uppercase tracking-wider text-muted"
          style={{ gridTemplateColumns: GRID }}
        >
          <span>Market</span>
          <span>Category</span>
          <span>Probability</span>
          <span className="text-right">Volume</span>
          <span className="text-right">Liquidity</span>
          <span>Actions</span>
        </div>
        {markets.map((m, i) => (
          <Row key={m.id} market={m} index={i} />
        ))}
      </div>
    </div>
  );
}

function Row({ market, index }: { market: MarketView; index: number }) {
  const openDetail = useStore((s) => s.openDetail);
  const openTrade = useStore((s) => s.openTrade);
  const mid = useMid(market.slug, 8000);
  const first = market.outcomes[0];
  const second = market.outcomes[1];
  const yesPrice = mid;
  const noPrice = 1 - mid;
  const { volume, liquidity } = marketMetrics(market);

  return (
    <div
      className="grid items-center gap-3 border-b border-line px-4 py-3 transition-colors hover:bg-bg-2 card-in"
      style={{
        gridTemplateColumns: GRID,
        animationDelay: `${index * 22}ms`,
      }}
    >
      <button
        type="button"
        onClick={() => openDetail(market.slug)}
        className="flex min-w-0 flex-col text-left"
      >
        <span className="truncate text-[13.5px] font-medium text-text">
          {market.title}
        </span>
        <span className="mt-[2px] truncate text-[11px] text-muted">
          resolves {fmtShortDate(market.resolve_after)}
        </span>
      </button>
      <span className="text-[11px] uppercase tracking-wider text-muted">
        {market.category}
      </span>
      <span className="font-mono text-[13px] text-text">
        {(yesPrice * 100).toFixed(1)}%
      </span>
      <span className="text-right font-mono text-[13px] text-text-2">
        {fmtMarketMetric(volume)}
      </span>
      <span className="text-right font-mono text-[13px] text-text-2">
        {fmtMarketMetric(liquidity)}
      </span>
      <div className="flex gap-2">
        <button
          className="btn-yn yes !py-[6px] !px-3 !text-[12px] flex-1"
          disabled={!first}
          onClick={() => first && openTrade(market, first.id, "buy")}
        >
          <span>YES</span>
          <span className="font-mono">{(yesPrice * 100).toFixed(1)}¢</span>
        </button>
        <button
          className="btn-yn no !py-[6px] !px-3 !text-[12px] flex-1"
          disabled={!second}
          onClick={() => second && openTrade(market, second.id, "buy")}
        >
          <span>NO</span>
          <span className="font-mono">{(noPrice * 100).toFixed(1)}¢</span>
        </button>
      </div>
    </div>
  );
}
